import { useState, useRef } from 'react';
import type {
  CommunityProfileProps,
  UserProfile,
  ActivityItem,
} from '../../../types/community-profile';
import {
  Trophy,
  TrendingUp,
  Users,
  Edit2,
  Save,
  X,
  Upload,
} from 'lucide-react';
import type {
  BadgeWithProgress,
  UserBadgeWithDetails,
} from '../../../lib/supabaseBadgeService';
import { BadgesCollection } from '../badges';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';
import { avatarService } from '../../../lib/avatarService';

type TabType = 'profile' | 'community' | 'badges';

export function CommunityProfile({
  userProfiles,
  leaderboards,
  currentUser,
  isOwnProfile = true,
  onViewProfile,
  allBadges = [],
  userBadges = [],
}: CommunityProfileProps & {
  allBadges?: BadgeWithProgress[];
  userBadges?: UserBadgeWithDetails[];
}) {
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  const earnedBadges = allBadges.filter((badge) => badge.isEarned);

  return (
    <div className='min-h-screen px-4 py-8 bg-slate-900 sm:px-6 lg:px-8'>
      <div className='max-w-6xl mx-auto'>
        <div className='overflow-hidden border shadow-2xl bg-slate-800 rounded-2xl border-slate-800'>
          <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

          <div className='p-6'>
            {activeTab === 'profile' && (
              <ProfileTab
                currentUser={currentUser}
                userProfile={userProfiles?.[currentUser.id]}
                earnedBadges={earnedBadges}
                isOwnProfile={isOwnProfile}
              />
            )}

            {activeTab === 'badges' && (
              <BadgesTab
                allBadges={allBadges}
                userBadges={userBadges}
                earnedBadges={earnedBadges}
              />
            )}

            {activeTab === 'community' && (
              <CommunityTab
                leaderboards={leaderboards}
                currentUser={currentUser}
                onViewProfile={onViewProfile}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface TabNavigationProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

function TabNavigation({ activeTab, onTabChange }: TabNavigationProps) {
  return (
    <div className='border-b border-slate-800'>
      <nav className='flex'>
        <TabButton
          active={activeTab === 'profile'}
          onClick={() => onTabChange('profile')}
          icon={<Users className='w-5 h-5' />}
          label='صفحتي'
        />
        <TabButton
          active={activeTab === 'community'}
          onClick={() => onTabChange('community')}
          icon={<Trophy className='w-5 h-5' />}
          label='الترتيب'
        />
        <TabButton
          active={activeTab === 'badges'}
          onClick={() => onTabChange('badges')}
          icon={<Trophy className='w-5 h-5' />}
          label='الانجازات'
        />
      </nav>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function TabButton({ active, onClick, icon, label }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-6 py-4 text-center font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
        active
          ? 'text-amber-400 border-b-2 border-amber-500 bg-amber-500/5'
          : 'text-slate-400 hover:text-amber-400 hover:bg-slate-800/50'
      }`}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

interface ProfileTabProps {
  currentUser: CommunityProfileProps['currentUser'];
  userProfile?: UserProfile;
  earnedBadges: BadgeWithProgress[];
  isOwnProfile: boolean;
}

function ProfileTab({
  currentUser,
  userProfile,
  earnedBadges,
  isOwnProfile,
}: ProfileTabProps) {
  return (
    <div className='space-y-6'>
      <ProfileHeader currentUser={currentUser} isOwnProfile={isOwnProfile} />
      <StatsGrid currentUser={currentUser} />
      <UserBio
        bio={userProfile?.bio || ''}
        userId={currentUser.id}
        isOwnProfile={isOwnProfile}
      />
      {userProfile?.recentActivity && (
        <RecentActivity activities={userProfile.recentActivity} />
      )}
      <RecentAchievements earnedBadges={earnedBadges} />
    </div>
  );
}

interface ProfileHeaderProps {
  currentUser: CommunityProfileProps['currentUser'];
  isOwnProfile: boolean;
}

function ProfileHeader({ currentUser, isOwnProfile }: ProfileHeaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    file: File;
    url: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = avatarService.validateAvatarFile(file);
    if (!validation.valid) {
      toast.error(validation.error!);
      return;
    }

    // Create preview
    const objectUrl = URL.createObjectURL(file);
    setPreviewFile({ file, url: objectUrl });

    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleUploadConfirm = async () => {
    if (!previewFile) return;

    setIsUploading(true);
    try {
      // Upload to storage
      const avatarUrl = await avatarService.uploadAvatar(
        currentUser.id,
        previewFile.file,
      );

      // Update database
      await avatarService.updateProfileAvatar(currentUser.id, avatarUrl);

      // Clean up preview
      URL.revokeObjectURL(previewFile.url);
      setPreviewFile(null);

      toast.success('رائع! تم تحديث صورة الملف الشخصي 🎉');

      // Reload page to show new avatar
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error('فشل رفع الصورة. حاول مرة أخرى');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelPreview = () => {
    if (previewFile) {
      URL.revokeObjectURL(previewFile.url);
      setPreviewFile(null);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!currentUser.avatarUrl) return;

    try {
      await avatarService.deleteAvatar(currentUser.id);
      await avatarService.updateProfileAvatar(currentUser.id, null);
      toast.success('تم إزالة الصورة');
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error('Avatar remove error:', error);
      toast.error('فشل إزالة الصورة');
    }
  };

  return (
    <div className='text-center'>
      <div className='relative inline-block mx-auto mb-4'>
        {/* Avatar Image or Initials */}
        <div className='relative w-24 h-24 group'>
          {currentUser.avatarUrl ? (
            <img
              src={currentUser.avatarUrl}
              alt={currentUser.name}
              className='object-cover w-24 h-24 transition-all duration-300 rounded-full shadow-lg ring-4 ring-amber-500/20 group-hover:scale-105 group-hover:shadow-2xl group-hover:shadow-amber-500/30'
            />
          ) : (
            <div className='flex items-center justify-center w-24 h-24 transition-all duration-300 rounded-full shadow-lg bg-gradient-to-br from-amber-500 to-rose-500 shadow-amber-500/20 group-hover:scale-105 group-hover:shadow-2xl group-hover:shadow-amber-500/30'>
              <span className='text-3xl font-bold text-white'>
                {currentUser.name.charAt(0)}
              </span>
            </div>
          )}

          {/* Upload Overlay (Own Profile Only) */}
          {isOwnProfile && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className='absolute inset-0 flex items-center justify-center transition-opacity duration-300 bg-black rounded-full opacity-0 group-hover:opacity-100 bg-opacity-60'
              title='تحميل صورة'
              aria-label='تحميل صورة الملف الشخصي'>
              <Upload className='w-6 h-6 text-white' />
            </button>
          )}
        </div>

        {/* Hidden File Input */}
        {isOwnProfile && (
          <input
            ref={fileInputRef}
            type='file'
            accept='image/jpeg,image/jpg,image/png,image/webp,image/gif'
            onChange={handleFileSelect}
            className='hidden'
          />
        )}
      </div>

      {/* Remove Avatar Button */}
      {isOwnProfile && currentUser.avatarUrl && (
        <button
          onClick={handleRemoveAvatar}
          className='mb-3 text-xs transition-colors duration-300 text-rose-400 hover:text-rose-300'>
          إزالة الصورة
        </button>
      )}

      <h1 className='mb-2 text-2xl font-bold text-white'>{currentUser.name}</h1>
      <p className='text-slate-400'>
        عضو منذ{' '}
        {new Date(currentUser.joinDate).toLocaleDateString('ar-EG', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
      </p>

      {/* Preview Modal */}
      {previewFile && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn'>
          <div className='w-full max-w-md p-6 transition-all duration-300 rounded-lg shadow-2xl bg-slate-800 animate-slideIn'>
            <h3 className='mb-4 text-lg font-semibold text-white'>
              معاينة الصورة
            </h3>
            <div className='mb-4 overflow-hidden rounded-lg'>
              <img
                src={previewFile.url}
                alt='Preview'
                className='object-cover w-full h-64 rounded-lg'
              />
            </div>
            <div className='flex justify-end gap-3'>
              <button
                onClick={handleCancelPreview}
                disabled={isUploading}
                className='px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg text-slate-300 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed'>
                إلغاء
              </button>
              <button
                onClick={handleUploadConfirm}
                disabled={isUploading}
                className='relative px-4 py-2 text-sm font-medium text-white transition-all duration-300 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed'>
                {isUploading ? (
                  <span className='flex items-center gap-2'>
                    <span className='inline-block w-4 h-4 border-2 border-white rounded-full animate-spin border-t-transparent'></span>
                    جاري الرفع...
                  </span>
                ) : (
                  'حفظ'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface StatsGridProps {
  currentUser: CommunityProfileProps['currentUser'];
}

function StatsGrid({ currentUser }: StatsGridProps) {
  return (
    <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
      <StatCard
        value={currentUser.wordsSubmitted}
        label='كلمات قدمتها'
        color='amber'
      />
      <StatCard
        value={currentUser.alternativesProposed}
        label='بدائل اقترحتها'
        color='rose'
      />
      <StatCard
        value={currentUser.votesCast}
        label='أصوات أدليت بها'
        color='slate'
      />
      <RankCard rank={currentUser.rank} />
    </div>
  );
}

interface StatCardProps {
  value: number;
  label: string;
  color: 'amber' | 'rose' | 'slate';
}

function StatCard({ value, label, color }: StatCardProps) {
  const colorClasses = {
    amber:
      'bg-gradient-to-br from-amber-500/10 to-rose-500/10 border-amber-500/20 text-amber-400',
    rose: 'bg-gradient-to-br from-rose-500/10 to-amber-500/10 border-rose-500/20 text-rose-400',
    slate: 'bg-slate-900 border-slate-700 text-slate-300',
  };

  const textColorClasses = {
    amber: 'text-amber-400/80',
    rose: 'text-rose-400/80',
    slate: 'text-slate-400',
  };

  return (
    <div className={`p-4 text-center border rounded-lg ${colorClasses[color]}`}>
      <div className='text-2xl font-bold'>{value}</div>
      <div className={`text-sm ${textColorClasses[color]}`}>{label}</div>
    </div>
  );
}

interface RankCardProps {
  rank: number;
}

function RankCard({ rank }: RankCardProps) {
  return (
    <div className='p-4 text-center border rounded-lg bg-gradient-to-br from-amber-500/10 to-rose-500/10 border-amber-500/20'>
      <div className='flex items-center justify-center gap-1 text-2xl font-bold text-white'>
        <TrendingUp className='w-5 h-5' />#{rank}
      </div>
      <div className='text-sm text-slate-400'>ترتيبك</div>
    </div>
  );
}

interface UserBioProps {
  bio: string;
  userId: string;
  isOwnProfile: boolean;
}

function UserBio({ bio, userId, isOwnProfile }: UserBioProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [bioText, setBioText] = useState(bio);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ bio: bioText })
        .eq('id', userId);

      if (error) throw error;

      toast.success('تم حفظ النبذة بنجاح');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating bio:', error);
      toast.error('فشل حفظ النبذة');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setBioText(bio);
    setIsEditing(false);
  };

  return (
    <div className='p-6 border rounded-lg bg-slate-900 border-slate-800'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-lg font-semibold text-white'>نبذة عني</h3>
        {!isEditing && isOwnProfile && (
          <button
            onClick={() => setIsEditing(true)}
            className='flex items-center gap-2 px-3 py-1.5 text-sm font-medium transition-all duration-300 rounded-lg text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'>
            <Edit2 className='w-4 h-4' />
            <span>تعديل</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className='space-y-3'>
          <textarea
            value={bioText}
            onChange={(e) => setBioText(e.target.value)}
            placeholder='اكتب نبذة عنك...'
            className='w-full px-4 py-3 leading-relaxed transition-all duration-300 border rounded-lg resize-none bg-slate-800 border-slate-700 text-slate-300 placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20'
            rows={4}
            maxLength={500}
          />
          <div className='flex items-center justify-between'>
            <span className='text-sm text-slate-500'>
              {bioText.length} / 500 حرف
            </span>
            <div className='flex gap-2'>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className='flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed'>
                <X className='w-4 h-4' />
                <span>إلغاء</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-all duration-300 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed'>
                <Save className='w-4 h-4' />
                <span>{isSaving ? 'جاري الحفظ...' : 'حفظ'}</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className='leading-relaxed text-slate-300'>
          {bio || 'لم يتم إضافة نبذة بعد.'}
        </p>
      )}
    </div>
  );
}

interface RecentActivityProps {
  activities: ActivityItem[];
}

function RecentActivity({ activities }: RecentActivityProps) {
  return (
    <div className='p-6 border rounded-lg bg-slate-900 border-slate-800'>
      <h3 className='mb-4 text-lg font-semibold text-white'>النشاط الأخير</h3>
      <div className='space-y-3'>
        {activities.map((activity, index) => (
          <ActivityItemDisplay key={index} activity={activity} />
        ))}
      </div>
    </div>
  );
}

interface ActivityItemDisplayProps {
  activity: ActivityItem;
}

function ActivityItemDisplay({ activity }: ActivityItemDisplayProps) {
  const activityColors: Record<ActivityItem['type'], string> = {
    vote: 'bg-amber-500',
    alternative: 'bg-rose-500',
    word: 'bg-slate-500',
    comment: 'bg-slate-400',
  };

  return (
    <div className='flex items-start gap-3 p-3 border rounded-lg bg-slate-800 border-slate-700'>
      <div
        className={`w-2 h-2 rounded-full mt-2 ${activityColors[activity.type]}`}
      />
      <div className='flex-1'>
        <p className='text-sm text-slate-300'>{activity.description}</p>
        <p className='mt-1 text-xs text-slate-500'>
          {new Date(activity.timestamp).toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
}

interface RecentAchievementsProps {
  earnedBadges: BadgeWithProgress[];
}

function RecentAchievements({ earnedBadges }: RecentAchievementsProps) {
  const tierLabels = {
    bronze: 'برونزي',
    silver: 'فضي',
    gold: 'ذهبي',
    platinum: 'بلاتيني',
    diamond: 'ألماس',
  };

  return (
    <div className='p-6 border rounded-lg bg-slate-900 border-slate-800'>
      <h3 className='mb-4 text-lg font-semibold text-white'>
        الإنجازات الحديثة
      </h3>
      {earnedBadges.length > 0 ? (
        <div className='flex flex-wrap gap-3'>
          {earnedBadges.slice(0, 6).map((badge) => (
            <BadgeCard
              key={badge.id}
              badge={badge}
              tierLabel={tierLabels[badge.tier]}
            />
          ))}
        </div>
      ) : (
        <p className='text-slate-400'>
          لم تحصل على أي شارات بعد. ابدأ بالمشاركة لكسب الشارات!
        </p>
      )}
    </div>
  );
}

interface BadgeCardProps {
  badge: BadgeWithProgress;
  tierLabel: string;
}

function BadgeCard({ badge, tierLabel }: BadgeCardProps) {
  return (
    <div className='flex items-center p-3 space-x-3 space-x-reverse border rounded-lg bg-slate-800 border-amber-500/30'>
      <span className='text-2xl'>{badge.icon}</span>
      <div>
        <div className='text-sm font-semibold text-amber-400'>{badge.name}</div>
        <div className='text-xs text-slate-400'>{tierLabel}</div>
      </div>
    </div>
  );
}

interface BadgesTabProps {
  allBadges: BadgeWithProgress[];
  userBadges: UserBadgeWithDetails[];
  earnedBadges: BadgeWithProgress[];
}

function BadgesTab({ allBadges, userBadges, earnedBadges }: BadgesTabProps) {
  return (
    <div className='space-y-6'>
      <BadgesHeader
        earnedCount={earnedBadges.length}
        remainingCount={allBadges.length - earnedBadges.length}
      />
      <BadgesCollection
        userBadges={userBadges}
        allBadges={allBadges}
        showLocked={true}
        layout='grid'
      />
    </div>
  );
}

interface BadgesHeaderProps {
  earnedCount: number;
  remainingCount: number;
}

function BadgesHeader({ earnedCount, remainingCount }: BadgesHeaderProps) {
  return (
    <div className='text-center'>
      <h1 className='mb-2 text-2xl font-bold text-white'>الانجازات</h1>
      <div className='flex justify-center gap-4 mt-4'>
        <BadgeCountStat
          value={earnedCount}
          label='شارات مكتسبة'
          color='amber'
        />
        <BadgeCountStat
          value={remainingCount}
          label='شارات متبقية'
          color='slate'
        />
      </div>
    </div>
  );
}

interface BadgeCountStatProps {
  value: number;
  label: string;
  color: 'amber' | 'slate';
}

function BadgeCountStat({ value, label, color }: BadgeCountStatProps) {
  const colorClass = color === 'amber' ? 'text-amber-400' : 'text-slate-400';

  return (
    <div className='text-center'>
      <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
      <div className='text-sm text-slate-400'>{label}</div>
    </div>
  );
}

interface CommunityTabProps {
  leaderboards: CommunityProfileProps['leaderboards'];
  currentUser: CommunityProfileProps['currentUser'];
  onViewProfile?: CommunityProfileProps['onViewProfile'];
}

function CommunityTab({
  leaderboards,
  currentUser,
  onViewProfile,
}: CommunityTabProps) {
  return (
    <div className='space-y-6'>
      <CommunityHeader />
      <OverallLeaderboard
        entries={leaderboards.overall}
        currentUser={currentUser}
        onViewProfile={onViewProfile}
      />
      <CategoryLeaderboards leaderboards={leaderboards} />
    </div>
  );
}

function CommunityHeader() {
  return (
    <div className='text-center'>
      <h1 className='mb-2 text-2xl font-bold text-white'>الترتيب</h1>
      <p className='text-slate-400'>
        أكتر الأعضاء مساهمة في الحركة اللغوية المصرية
      </p>
    </div>
  );
}

interface OverallLeaderboardProps {
  entries: CommunityProfileProps['leaderboards']['overall'];
  currentUser: CommunityProfileProps['currentUser'];
  onViewProfile?: CommunityProfileProps['onViewProfile'];
}

function OverallLeaderboard({
  entries,
  currentUser,
  onViewProfile,
}: OverallLeaderboardProps) {
  return (
    <div className='overflow-hidden border rounded-lg bg-slate-900 border-slate-800'>
      <div className='p-6 border-b border-slate-800'>
        <h3 className='text-lg font-semibold text-white'>الترتيب</h3>
      </div>
      <div className='divide-y divide-slate-800'>
        {entries.map((entry, index) => (
          <LeaderboardEntry
            key={entry.userId}
            entry={entry}
            index={index}
            isCurrentUser={entry.userId === currentUser.id}
            onViewProfile={onViewProfile}
          />
        ))}
      </div>
    </div>
  );
}

interface LeaderboardEntryProps {
  entry: CommunityProfileProps['leaderboards']['overall'][number];
  index: number;
  isCurrentUser: boolean;
  onViewProfile?: CommunityProfileProps['onViewProfile'];
}

function LeaderboardEntry({
  entry,
  index,
  isCurrentUser,
  onViewProfile,
}: LeaderboardEntryProps) {
  return (
    <div
      className={`p-4 flex items-center gap-4 hover:bg-slate-800 transition-all duration-300 ${
        isCurrentUser ? 'bg-amber-500/10 border-r-4 border-amber-500' : ''
      }`}>
      <RankBadge rank={entry.rank} index={index} />
      <UserAvatar name={entry.name} avatarUrl={entry.avatarUrl} />
      <UserInfo
        name={entry.name}
        totalScore={entry.totalScore || 0}
        isCurrentUser={isCurrentUser}
      />
      {!isCurrentUser && (
        <ViewProfileButton onClick={() => onViewProfile?.(entry.userId)} />
      )}
    </div>
  );
}

interface RankBadgeProps {
  rank: number;
  index: number;
}

function RankBadge({ rank, index }: RankBadgeProps) {
  const getRankStyle = (index: number) => {
    if (index === 0)
      return 'bg-gradient-to-br from-amber-400 to-rose-500 text-white';
    if (index === 1) return 'bg-slate-400 text-white';
    if (index === 2) return 'bg-amber-600 text-white';
    return 'bg-slate-800 text-slate-400';
  };

  return (
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${getRankStyle(index)}`}>
      {rank}
    </div>
  );
}

interface UserAvatarProps {
  name: string;
  avatarUrl: string | null;
}

function UserAvatar({ name, avatarUrl }: UserAvatarProps) {
  return (
    <>
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className='object-cover w-10 h-10 rounded-full ring-2 ring-amber-500/20'
        />
      ) : (
        <div className='flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-rose-500'>
          <span className='text-sm font-bold text-white'>{name.charAt(0)}</span>
        </div>
      )}
    </>
  );
}

interface UserInfoProps {
  name: string;
  totalScore: number;
  isCurrentUser: boolean;
}

function UserInfo({ name, totalScore, isCurrentUser }: UserInfoProps) {
  return (
    <div className='flex-1'>
      <p className='font-medium text-white'>
        {name}
        {isCurrentUser && (
          <span className='mr-2 text-xs text-amber-400'>(أنت)</span>
        )}
      </p>
      <p className='text-sm text-slate-400'>{totalScore} نقطة</p>
    </div>
  );
}

interface ViewProfileButtonProps {
  onClick: () => void;
}

function ViewProfileButton({ onClick }: ViewProfileButtonProps) {
  return (
    <button
      onClick={onClick}
      className='px-3 py-1 text-sm transition-all duration-300 rounded-md bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'>
      عرض الملف
    </button>
  );
}

interface CategoryLeaderboardsProps {
  leaderboards: CommunityProfileProps['leaderboards'];
}

function CategoryLeaderboards({ leaderboards }: CategoryLeaderboardsProps) {
  const categories = [
    {
      key: 'wordsSubmitted',
      title: 'كلمات مقدمة',
      data: leaderboards.wordsSubmitted,
    },
    {
      key: 'alternativesProposed',
      title: 'بدائل مقترحة',
      data: leaderboards.alternativesProposed,
    },
    {
      key: 'votesCast',
      title: 'أصوات أدلى بها',
      data: leaderboards.votesCast,
    },
  ];

  return (
    <div className='grid gap-6 md:grid-cols-3'>
      {categories.map((category) => (
        <CategoryLeaderboard
          key={category.key}
          title={category.title}
          entries={category.data}
        />
      ))}
    </div>
  );
}

interface CategoryLeaderboardProps {
  title: string;
  entries: CommunityProfileProps['leaderboards']['overall'];
}

function CategoryLeaderboard({ title, entries }: CategoryLeaderboardProps) {
  return (
    <div className='overflow-hidden border rounded-lg bg-slate-900 border-slate-800'>
      <div className='p-4 border-b border-slate-800 bg-slate-800/50'>
        <h4 className='font-semibold text-center text-white'>{title}</h4>
      </div>
      <div className='p-4 space-y-3'>
        {entries.slice(0, 3).map((entry, index) => (
          <CategoryLeaderboardEntry
            key={entry.userId}
            entry={entry}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

interface CategoryLeaderboardEntryProps {
  entry: CommunityProfileProps['leaderboards']['overall'][number];
  index: number;
}

function CategoryLeaderboardEntry({
  entry,
  index,
}: CategoryLeaderboardEntryProps) {
  const getRankColor = (index: number) => {
    if (index === 0) return 'bg-amber-500 text-white';
    if (index === 1) return 'bg-slate-500 text-white';
    return 'bg-rose-500 text-white';
  };

  return (
    <div className='flex items-center justify-between'>
      <div className='flex items-center gap-2'>
        <span
          className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${getRankColor(index)}`}>
          {entry.rank}
        </span>
        <span className='text-sm font-medium truncate text-slate-300'>
          {entry.name}
        </span>
      </div>
      <span className='text-sm font-bold text-white'>
        {entry.count || entry.totalScore}
      </span>
    </div>
  );
}
